// Подбор репетиторов по заявке родителя «Не нашли рядом?» (карта «Evə yaxın»).
//
// Чистые функции без базы и сети: их зовёт сервер (map-match.server.ts) и
// проверка scripts/verify-map-match.mts. Название matchTutors — то же, что
// match_tutors(request_id) в задаче: подбор живёт здесь, а не в SQL, чтобы
// правила проверялись в prebuild вместе с остальной картой.
//
// Кандидаты: репетитор, опубликован, не на паузе, не удалён владельцем, тот
// же город (кроме онлайна — ему город не важен).
//
// Жёсткие фильтры: предмет, класс (или классы не указаны), формат, город,
// бюджет (цена урока не выше бюджета +20 %, неизвестная цена проходит).
// Меньше трёх — ослабляем место (соседние районы и онлайн), затем бюджет.
// Ноль и после этого — no_match.
//
// Язык обучения (03.11.2026) — тоже жёсткий: если родитель выбрал язык, в
// подборку идут только анкеты, ведущие уроки на этом языке: «Язык уроков»
// анкеты (teach_langs, несколько), а если он не указан — map_partners.lang
// (src/lib/teach-langs.ts). Только если с языком и после всех ослаблений
// меньше трёх — добираем до трёх анкетами на других языках (relaxed «lang»,
// у такой анкеты otherLangs — языки её уроков, письмо и страница пишут
// «ведёт уроки на …»). Они всегда ниже своих.
//
// Баллы 0–100: место 35, цена 25, язык 10, полнота анкеты 10, пробный 5,
// свежесть 5, плюс штраф ротации до −10 за ≥3 заявок за 7 дней.

import { DISTRICTS, METRO, BAKU_CITY } from "@/data/map-taxonomy";
import { perLesson } from "@/lib/price-format";
import { lessonLangs } from "@/lib/teach-langs";

export type RequestFormat = "any" | "tutor" | "home" | "online";
export const REQUEST_FORMATS: RequestFormat[] = ["any", "tutor", "home", "online"];
export type BudgetUnit = "lesson" | "month";

/** Что из заявки нужно подбору. */
export type MatchRequest = {
  subject_code: string;
  grade: string | null;
  city: string | null;
  district: string | null;
  format: RequestFormat;
  online_ok: boolean;
  teach_lang: "az" | "ru" | "en" | null;
  budget_azn: number | null;
  budget_unit: BudgetUnit;
};

/** Что из анкеты нужно подбору (map_partners, сервис-роль). */
export type MatchCandidate = {
  id: string;
  kind: "tutor" | "club";
  status: string;
  subjects: string[];
  grades: string[];
  formats: string[];
  address_mode: "building" | "travel" | "online";
  city: string | null;
  district: string | null;
  travel_districts: string[];
  lat: number | null;
  lng: number | null;
  price_azn: number | null;
  price_unit: string;
  lessons_per_unit: number | null;
  price_confirmed: boolean;
  trial_free: boolean;
  lang: "az" | "ru" | "en";
  /** «Язык уроков» (20261118120000); пусто, null или нет (до наката) — берётся lang. */
  teach_langs?: ("az" | "ru" | "en")[] | null;
  photo_url: string | null;
  about: string | null;
  updated_at: string | null;
  is_paused: boolean;
  deleted: boolean;
};

export type Relaxation = "place" | "budget" | "lang";

export type ScoredCandidate = {
  id: string;
  score: number;
  parts: { place: number; price: number; lang: number; profile: number; trial: number; fresh: number; rotation: number };
  /** Добрана на другом языке: языки её уроков. Нет поля — язык совпал (или не выбран). */
  otherLangs?: ("az" | "ru" | "en")[];
};

export type MatchResult = {
  picked: ScoredCandidate[];
  relaxed: Relaxation[];
  /** Сколько прошло жёсткие фильтры на каждом шаге — для /admin. */
  pool: number;
};

export const MATCH_MAX = 5;
export const MATCH_MIN = 3;
/** Немного выше бюджета — частичный балл и всё ещё проходит фильтр. */
export const BUDGET_TOLERANCE = 1.2;
/** Бюджет «в месяц» без числа уроков: считаем два урока в неделю. */
export const BUDGET_LESSONS_PER_MONTH = 8;
/** Соседний район — центр не дальше этого от центра района заявки. */
export const NEIGHBOR_KM = 8;
/** Ротация: с этого числа заявок за 7 дней начинается штраф. */
export const ROTATION_FROM = 3;

type Point = { lat: number; lng: number };
const centers = new Map<string, Point>(
  (DISTRICTS as unknown as { code: string; lat?: number; lng?: number }[])
    .filter((d) => typeof d.lat === "number" && typeof d.lng === "number")
    .map((d) => [d.code, { lat: d.lat as number, lng: d.lng as number }]),
);

/** Центр района из справочника. null — района нет или у него нет точки. */
export const districtCenter = (code: string | null): Point | null => (code ? (centers.get(code) ?? null) : null);

export function haversineKm(a: Point, b: Point): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/** Районы, чьи центры не дальше NEIGHBOR_KM от центра этого (сам район не входит). */
export function neighborDistricts(code: string): string[] {
  const c = districtCenter(code);
  if (!c) return [];
  return [...centers.entries()].filter(([k, p]) => k !== code && haversineKm(c, p) <= NEIGHBOR_KM).map(([k]) => k);
}

/** Бюджет заявки в пересчёте на один урок. null — бюджета нет. */
export function budgetPerLesson(r: Pick<MatchRequest, "budget_azn" | "budget_unit">): number | null {
  if (r.budget_azn === null || !(r.budget_azn > 0)) return null;
  return r.budget_unit === "month" ? r.budget_azn / BUDGET_LESSONS_PER_MONTH : r.budget_azn;
}

/** Цена урока анкеты: месяц и пакет делятся на число уроков. Только подтверждённый формат. */
export const candidatePerLesson = (c: MatchCandidate): number | null =>
  perLesson({ kind: c.kind, price_azn: c.price_azn, price_unit: c.price_unit, lessons_per_unit: c.lessons_per_unit, price_confirmed: c.price_confirmed });

/** Ведёт ли анкета уроки на этом языке: «Язык уроков», а если не указан — язык анкеты. */
export const teaches = (c: MatchCandidate, lang: "az" | "ru" | "en") => lessonLangs(c).includes(lang);

const isOnline = (c: MatchCandidate) => c.address_mode === "online" || c.formats.includes("online");
const isBaku = (city: string | null) => (city ?? BAKU_CITY) === BAKU_CITY;

/** Кандидат вообще: репетитор, опубликован, не на паузе, не удалён. */
export const isEligible = (c: MatchCandidate) => c.kind === "tutor" && c.status === "published" && !c.is_paused && !c.deleted;

export const teachesSubject = (c: MatchCandidate, subject: string) => c.subjects.includes(subject);
export const fitsGrade = (c: MatchCandidate, grade: string | null) => !grade || c.grades.length === 0 || c.grades.includes(grade);

/**
 * Место и формат вместе. wide — после ослабления «места»: соседние районы и
 * онлайн подходят тоже.
 *   online — онлайн-анкета, город не важен.
 *   home   — приезжает в район заявки (вне Баку — travel по тому же городу).
 *   tutor  — занятие у репетитора: building в том же городе (район — балл, не фильтр).
 *   any    — любое из трёх.
 * online_ok добавляет онлайн к home и tutor.
 */
export function fitsPlace(c: MatchCandidate, r: MatchRequest, wide = false): boolean {
  // Онлайн (анкета online или формат «онлайн» у building/travel) — в любом
  // городе, если заявка онлайн принимает.
  const acceptsOnline = r.format === "online" || r.format === "any" || r.online_ok || wide;
  if (isOnline(c) && acceptsOnline) return true;
  if (r.format === "online") return false;
  if ((c.city ?? BAKU_CITY) !== (r.city ?? BAKU_CITY)) return false;
  const home = (): boolean => {
    if (c.address_mode === "online") return false;
    if (!isBaku(r.city)) return c.address_mode === "travel";
    if (!r.district) return c.travel_districts.length > 0;
    if (c.travel_districts.includes(r.district)) return true;
    return wide && neighborDistricts(r.district).some((d) => c.travel_districts.includes(d));
  };
  const atTutor = (): boolean => {
    if (c.address_mode !== "building") return false;
    if (c.formats.length && !c.formats.includes("in_person") && c.formats.includes("online")) return false;
    return true;
  };
  if (r.format === "home") return home();
  if (r.format === "tutor") return atTutor();
  return home() || atTutor();
}

/** Цена в бюджете (+20 %) или неизвестна. */
export function fitsBudget(c: MatchCandidate, r: MatchRequest): boolean {
  const b = budgetPerLesson(r);
  if (b === null) return true;
  const p = candidatePerLesson(c);
  return p === null || p <= b * BUDGET_TOLERANCE;
}

/** Место, 0–35: район совпал, приезжает или рядом по точке. Онлайн на онлайн-заявку — полный балл. */
export function placeScore(c: MatchCandidate, r: MatchRequest): number {
  if (r.format === "online") return isOnline(c) ? 35 : 0;
  if (c.address_mode === "online") return 12;
  if (!isBaku(r.city)) return 25;
  if (!r.district) return 20;
  if (c.district === r.district || c.travel_districts.includes(r.district)) return 35;
  const center = districtCenter(r.district);
  if (center && c.lat !== null && c.lng !== null) {
    const km = haversineKm(center, { lat: c.lat, lng: c.lng });
    if (km <= 2) return 35;
    if (km >= 15) return 0;
    return Math.round(35 * (1 - (km - 2) / 13) * 10) / 10;
  }
  if (neighborDistricts(r.district).some((d) => d === c.district || c.travel_districts.includes(d))) return 18;
  return 5;
}

/** Цена, 0–25: в бюджете 25, до +20 % — 12, выше — 0, неизвестна — 8, бюджета нет — 15 у всех. */
export function priceScore(c: MatchCandidate, r: MatchRequest): number {
  const b = budgetPerLesson(r);
  if (b === null) return 15;
  const p = candidatePerLesson(c);
  if (p === null) return 8;
  if (p <= b) return 25;
  if (p <= b * BUDGET_TOLERANCE) return 12;
  return 0;
}

/** Полнота анкеты, 0–10: фото 4, «о себе» 3, подтверждённая цена 3. */
export const profileScore = (c: MatchCandidate) => (c.photo_url ? 4 : 0) + (c.about && c.about.trim().length >= 20 ? 3 : 0) + (c.price_confirmed ? 3 : 0);

/** Свежесть, 0–5: правилась за 30 дней — 5, за 90 — 2.5. */
export function freshScore(c: MatchCandidate, now: number): number {
  if (!c.updated_at) return 0;
  const days = (now - Date.parse(c.updated_at)) / 86_400_000;
  if (days <= 30) return 5;
  if (days <= 90) return 2.5;
  return 0;
}

/** Ротация, 0…−10: 3 заявки за 7 дней — −4, 4 — −8, 5 и больше — −10. */
export const rotationPenalty = (recent: number) => (recent < ROTATION_FROM ? 0 : -Math.min(10, (recent - ROTATION_FROM + 1) * 4));

export function scoreCandidate(c: MatchCandidate, r: MatchRequest, recent: number, now: number): ScoredCandidate {
  const parts = {
    place: placeScore(c, r),
    price: priceScore(c, r),
    lang: r.teach_lang && teaches(c, r.teach_lang) ? 10 : 0,
    profile: profileScore(c),
    trial: c.trial_free ? 5 : 0,
    fresh: freshScore(c, now),
    rotation: rotationPenalty(recent),
  };
  const score = Math.max(0, Math.round((parts.place + parts.price + parts.lang + parts.profile + parts.trial + parts.fresh + parts.rotation) * 10) / 10);
  return { id: c.id, score, parts };
}

/**
 * Подбор. recentCounts — сколько подборок каждый попал за 7 дней.
 * exclude — уже предложенные (повторная подборка после «не нашли»).
 */
export function matchTutors(
  r: MatchRequest,
  candidates: readonly MatchCandidate[],
  opts: { recentCounts?: ReadonlyMap<string, number>; now?: number; exclude?: ReadonlySet<string> } = {},
): MatchResult {
  const now = opts.now ?? Date.now();
  const base = candidates.filter((c) => isEligible(c) && !opts.exclude?.has(c.id) && teachesSubject(c, r.subject_code) && fitsGrade(c, r.grade));
  const same = r.teach_lang ? base.filter((c) => teaches(c, r.teach_lang!)) : base;
  const own = ladder(r, same);
  const ranked = rank(r, own.pool, opts.recentCounts, now);
  if (!r.teach_lang || own.pool.length >= MATCH_MIN) {
    return { picked: ranked.slice(0, MATCH_MAX), relaxed: own.pool.length ? own.relaxed : ["place", "budget"], pool: own.pool.length };
  }
  // С языком меньше трёх — добираем до трёх другими языками, их тоже ослабляя по шагам.
  const others = ladder(r, base.filter((c) => !teaches(c, r.teach_lang!)));
  const fill = rank(r, others.pool, opts.recentCounts, now)
    .slice(0, MATCH_MIN - own.pool.length)
    .map((s) => ({ ...s, otherLangs: lessonLangs(others.pool.find((c) => c.id === s.id)!) }));
  const picked = [...ranked, ...fill];
  // С языком меньше трёх — значит, свой пул уже прошёл все шаги ослабления.
  const relaxed: Relaxation[] = fill.length ? ["place", "budget", "lang"] : ["place", "budget"];
  return { picked, relaxed: picked.length ? relaxed : ["place", "budget"], pool: own.pool.length + others.pool.length };
}

/** Жёсткие фильтры места и бюджета, ослабляемые по шагам до трёх. */
function ladder(r: MatchRequest, base: readonly MatchCandidate[]): { pool: MatchCandidate[]; relaxed: Relaxation[] } {
  const steps: { relaxed: Relaxation[]; pass: (c: MatchCandidate) => boolean }[] = [
    { relaxed: [], pass: (c) => fitsPlace(c, r) && fitsBudget(c, r) },
    { relaxed: ["place"], pass: (c) => fitsPlace(c, r, true) && fitsBudget(c, r) },
    { relaxed: ["place", "budget"], pass: (c) => fitsPlace(c, r, true) },
  ];
  let pool: MatchCandidate[] = [];
  let relaxed: Relaxation[] = [];
  for (const s of steps) {
    pool = base.filter(s.pass);
    relaxed = s.relaxed;
    if (pool.length >= MATCH_MIN) break;
  }
  return { pool, relaxed };
}

function rank(r: MatchRequest, pool: readonly MatchCandidate[], recentCounts: ReadonlyMap<string, number> | undefined, now: number): ScoredCandidate[] {
  return pool
    .map((c) => ({ c, s: scoreCandidate(c, r, recentCounts?.get(c.id) ?? 0, now) }))
    .sort((a, b) => b.s.score - a.s.score || (Date.parse(b.c.updated_at ?? "") || 0) - (Date.parse(a.c.updated_at ?? "") || 0) || a.c.id.localeCompare(b.c.id))
    .map((x) => x.s);
}

// ─── Защита ───────────────────────────────────────────────────────────

export const phoneDigits = (phone: string) => phone.replace(/\D/g, "").replace(/^0+/, "").slice(-9);
export const normEmail = (email: string | null | undefined) => (email ?? "").trim().toLowerCase();

/** Ключ повтора: телефон (последние 9 цифр) и предмет. Без телефона — почта и предмет. */
export const dedupeKey = (phone: string, subject: string, email?: string | null) => {
  const digits = phoneDigits(phone);
  return digits ? `${digits}|${subject || "-"}` : `@${normEmail(email)}|${subject || "-"}`;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Заявка «Не нашли рядом?» принимается, если есть куда прислать подборку
 * (04.11.2026): телефон (WhatsApp, от 7 цифр) ИЛИ почта с согласием. Одно
 * правило для формы и сервера.
 */
export function contactOk(r: { phone?: string | null; email?: string | null; consent?: boolean | null }): boolean {
  const phoneOk = (r.phone ?? "").replace(/\D/g, "").length >= 7;
  const email = (r.email ?? "").trim();
  return phoneOk || (EMAIL_RE.test(email) && !!r.consent);
}

const INTERNAL_EMAIL = /@(yolutap\.com|notify\.yolutap\.com|example\.(com|org|net))$/i;
const TEST_WORD = /(^|[^\p{L}])(test|тест|sınaq|sinaq)([^\p{L}]|$)/iu;

/**
 * Тестовая заявка: «test» в комментарии, внутренний адрес, или телефон/почта
 * совпадают с анкетой репетитора. Возвращает причину или null.
 */
export function testReason(
  r: { comment: string | null; email: string | null; phone: string },
  partners: readonly { phone: string | null; whatsapp: string | null; email: string | null }[],
): string | null {
  if (r.comment && TEST_WORD.test(r.comment)) return "comment";
  const email = normEmail(r.email);
  if (email && INTERNAL_EMAIL.test(email)) return "internal_email";
  const digits = phoneDigits(r.phone);
  if (digits && partners.some((p) => (p.phone && phoneDigits(p.phone) === digits) || (p.whatsapp && phoneDigits(p.whatsapp) === digits))) return "partner_phone";
  if (email && partners.some((p) => normEmail(p.email) === email)) return "partner_email";
  return null;
}

/** Не больше стольких заявок с одного телефона или адреса за сутки. */
export const RATE_LIMIT_PER_DAY = 5;

/** Метро рядом с анкетой — подпись в письме, если района нет. */
export const metroLabel = (code: string | null) => (code ? (METRO.find((m) => m.code === code) ?? null) : null);
